import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Lock, X, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";

// Fire Meta Pixel Purchase event once per loyalty card purchase. Uses
// localStorage as a durable guard so re-opening the card link never
// double-counts. Always sends an eventID for future Conversions API dedup.
function trackPurchaseOnce(cardId, valueEur, sessionId) {
  if (!cardId || !valueEur) return;
  const key = `fbq_purchase_loyalty_${cardId}`;
  try {
    if (typeof window === "undefined") return;
    const store = window.localStorage || window.sessionStorage;
    if (store && store.getItem(key)) return;
    if (typeof window.fbq !== "function") return;
    const eventID = sessionId || cardId;
    window.fbq(
      "track",
      "Purchase",
      { value: Number(valueEur), currency: "EUR" },
      { eventID }
    );
    if (store) store.setItem(key, "1");
  } catch (_) {
    /* pixel best-effort */
  }
}

const CARD_BG =
  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/fqj3z62b_1d61bcd0-9b05-4540-ba9e-f83806528b6c.jpeg";

// Tier-specific hero art. Falls back to the classic flyer for `classic-6plus6`
// (legacy paid holders) so the pixel-overlay slot design keeps working, and to
// each tier's own marketing image for the 3 active tiers.
const TIER_ART = {
  "classic-6plus6": CARD_BG,
  "strip-starter": "/loyalty/strip-starter.jpg",
  "double-duo": "/loyalty/double-duo.jpg",
  "vip-six": "/loyalty/vip-six.jpg",
};

// Approx % positions of the 12 white circles in the reference image
// (image is square; values are % of the image box, NOT the viewport).
const SLOT_POSITIONS = {
  drink: [
    { x: 24.1, y: 47.8 },
    { x: 34.5, y: 47.8 },
    { x: 44.8, y: 47.8 },
    { x: 55.2, y: 47.8 },
    { x: 65.6, y: 47.8 },
    { x: 75.9, y: 47.8 },
  ],
  shot: [
    { x: 24.1, y: 68.0 },
    { x: 34.5, y: 68.0 },
    { x: 44.8, y: 68.0 },
    { x: 55.2, y: 68.0 },
    { x: 65.6, y: 68.0 },
    { x: 75.9, y: 68.0 },
  ],
};
// Diameter of one slot relative to image width
const SLOT_SIZE_PCT = 9.5;

/**
 * Soho 6+6 Digital Drink Card — pixel-matches the printed flyer artwork by
 * using the boss's reference image as the background and overlaying 12 tap
 * targets exactly on the white circles. A "punched" slot stamps a big neon
 * pink X over the circle. PIN modal flow unchanged.
 */
export default function LoyaltyCardPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { t } = useLang();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinModal, setPinModal] = useState(null);
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [payState, setPayState] = useState(sessionId ? "polling" : "idle");

  const loadCard = async () => {
    try {
      const { data } = await api.get(`/loyalty/card/${token}`);
      setCard(data.card);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCard();
  }, [token]);

  // Poll Stripe status when arriving from Checkout redirect.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          setTimeout(async () => {
            await loadCard();
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
          }, 900);
          return;
        }
        if (data.status === "expired") {
          setPayState("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setPayState("failed");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setPayState("failed");
          return;
        }
        setTimeout(tick, 2500);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Fire Meta Pixel Purchase event once when card payment is confirmed.
  useEffect(() => {
    if (!card) return;
    if (card.payment_status !== "paid") return;
    trackPurchaseOnce(card.id || token, card.total_price_eur, sessionId);
  }, [card, sessionId, token]);

  // Modal action drives which backend endpoint gets hit.
  //   punch          — bar staff, PIN 1269, marks slot used
  //   unpunch        — supervisor,  PIN 3589, un-marks a used slot (error fix)
  //   confirm-final  — bar staff, PIN 1269, marks the 70% cash as collected
  const openPin = (mode, slot_type, slot_index) => {
    setPin("");
    setPinModal({ mode, slot_type, slot_index });
  };

  const submitPin = async (e) => {
    e?.preventDefault?.();
    if (!pinModal) return;
    setSubmitting(true);
    try {
      let endpoint, body;
      if (pinModal.mode === "punch") {
        endpoint = `/loyalty/card/${token}/punch`;
        body = { slot_type: pinModal.slot_type, slot_index: pinModal.slot_index, pin };
      } else if (pinModal.mode === "unpunch") {
        endpoint = `/loyalty/card/${token}/unpunch`;
        body = { slot_type: pinModal.slot_type, slot_index: pinModal.slot_index, pin };
      } else {
        // confirm-final
        endpoint = `/loyalty/card/${token}/confirm-final`;
        body = { pin };
      }
      const { data } = await api.post(endpoint, body);
      setCard(data.card);
      setPinModal(null);
      if (pinModal.mode === "punch") toast.success(t("card.punch.ok"));
      else if (pinModal.mode === "unpunch") toast.success("Drink un-redeemed");
      else toast.success("Final cash payment confirmed — card fully paid");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-neon font-heading text-2xl tracking-widest">
        LOADING…
      </div>
    );
  }
  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <AlertTriangle className="text-neon mx-auto" size={36} />
          <div className="mt-4 font-heading text-2xl uppercase">{t("card.notfound")}</div>
        </div>
      </div>
    );
  }

  const drinksUsed = card.drinks.filter((s) => s.used).length;
  const shotsUsed = card.shots.filter((s) => s.used).length;
  const isActive = card.status === "active";

  return (
    <div
      data-testid="loyalty-card-page"
      className="min-h-screen text-white"
      style={{ background: "#000" }}
    >
      <div className="max-w-[640px] mx-auto px-3 sm:px-5 pt-3 pb-10">
        {/* Payment status banner (during / after Stripe redirect) */}
        {payState === "polling" ? (
          <div
            className="mb-3 rounded-2xl border border-[#FF00A6]/60 bg-[#FF00A6]/10 backdrop-blur px-4 py-3 flex items-center gap-3"
            data-testid="loyalty-payment-polling-banner"
          >
            <Loader2 className="w-4 h-4 text-[#FF00A6] animate-spin" />
            <div className="text-xs text-white">
              <span className="font-bold">Confirming your payment…</span>
              <div className="text-white/70 mt-0.5">Please don&apos;t close this page.</div>
            </div>
          </div>
        ) : null}
        {payState === "paid" ? (
          <div
            className="mb-3 rounded-2xl border border-emerald-400/60 bg-emerald-400/10 backdrop-blur px-4 py-3 flex items-center gap-3"
            data-testid="loyalty-payment-success-banner"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <div className="text-xs text-white">
              <span className="font-bold">Payment confirmed. Your Drink Pass is ready.</span>
            </div>
          </div>
        ) : null}
        {payState === "expired" || payState === "failed" ? (
          <div
            className="mb-3 rounded-2xl border border-amber-400/60 bg-amber-400/10 backdrop-blur px-4 py-3"
            data-testid="loyalty-payment-warning-banner"
          >
            <div className="text-xs text-white font-bold">Payment not confirmed yet — refresh in a moment.</div>
          </div>
        ) : null}
        {/* Guest name + PAID pill (top-right) above the artwork */}
        <div className="flex items-center justify-between gap-3 mb-3 px-1">
          <div className="text-xs uppercase tracking-[0.32em] text-white/70">
            {card.name}
          </div>
          {card.final_paid ? (
            <span
              data-testid="loyalty-fully-paid-banner"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white"
              style={{
                background: "linear-gradient(135deg,#0aa84a,#22c55e)",
                boxShadow: "0 0 12px rgba(34,197,94,0.55)",
                border: "1px solid #4ade80",
              }}
            >
              <CheckCircle2 size={12} className="text-white" /> Paid · Ready
            </span>
          ) : (
            <span
              className={
                "px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border " +
                (isActive
                  ? "border-emerald-400/40 text-emerald-300"
                  : card.status === "done"
                  ? "border-white/30 text-white/60"
                  : "border-yellow-400/50 text-yellow-300")
              }
            >
              {card.status}
            </span>
          )}
        </div>

        {/* Pending payment banner (only when NOT yet paid). Paid cards use the
            slim green pill above — no big banner needed. */}
        {!card.final_paid && (
          <div
            className="mb-4 rounded-3xl px-5 py-5 bg-black/70 backdrop-blur text-center"
            style={{
              border: "2px solid #FF1B8D",
              boxShadow: "0 0 24px rgba(255,27,141,0.35)",
            }}
            data-testid="loyalty-balance-box"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Payment Status
            </div>
            <div
              className="font-heading text-xl sm:text-2xl text-[#FF1B8D] font-bold"
              data-testid="loyalty-balance-amount"
            >
              €{Number(card.total_price_eur || 0).toFixed(2)} due
            </div>
            <div className="mt-2 text-[11px] text-white/60 leading-relaxed">
              Complete your online payment to activate this Drink Pass.
            </div>
          </div>
        )}

        {/* The artwork + slot overlay. Legacy Classic 6+6 flyers have the
            circles painted onto the printed artwork, so we overlay the tap
            targets at fixed % positions. The 3 new tiers use their own
            marketing image as a hero and render the punch slots below as a
            plain neon grid — the drink/shot slot COUNT is always driven by
            `card.drinks.length` / `card.shots.length` (materialised from the
            tier at purchase time). */}
        {(() => {
          const isClassic = card.tier_id === "classic-6plus6";
          const cardArt = TIER_ART[card.tier_id] || TIER_ART["strip-starter"];
          const slotLabel = card.tier_name || "Drinks Ticket";
          return isClassic ? (
            <div
              className="relative w-full mx-auto"
              style={{ aspectRatio: "1 / 1", maxWidth: 640 }}
            >
              <img loading="lazy" decoding="async" src={cardArt}
                alt={slotLabel}
                data-testid="loyalty-card-art"
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
              />
              {card.drinks.map((slot, i) => (
                <SlotOverlay
                  key={`drink-${i}`}
                  pos={SLOT_POSITIONS.drink[i]}
                  used={slot.used}
                  disabled={!isActive && !slot.used}
                  onClick={() => openPin(slot.used ? "unpunch" : "punch", "drink", i)}
                  testid={`slot-drink-${i}`}
                  ariaLabel={`Drink ${i + 1}`}
                />
              ))}
              {card.shots.map((slot, i) => (
                <SlotOverlay
                  key={`shot-${i}`}
                  pos={SLOT_POSITIONS.shot[i]}
                  used={slot.used}
                  disabled={!isActive && !slot.used}
                  onClick={() => openPin(slot.used ? "unpunch" : "punch", "shot", i)}
                  testid={`slot-shot-${i}`}
                  ariaLabel={`Shot ${i + 1}`}
                />
              ))}
            </div>
          ) : (
            <div>
              <img loading="lazy" decoding="async" src={cardArt}
                alt={slotLabel}
                data-testid="loyalty-card-art"
                draggable={false}
                className="block w-full max-w-[640px] mx-auto rounded-2xl select-none"
                style={{
                  boxShadow: "0 0 30px rgba(255,27,141,0.35)",
                  border: "1px solid rgba(255,27,141,0.35)",
                }}
              />
              {/* Plain neon slot grid below the hero for the 3 new tiers */}
              {card.drinks.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold mb-2 text-center">
                    Drinks
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {card.drinks.map((slot, i) => (
                      <PlainSlot
                        key={`drink-${i}`}
                        used={slot.used}
                        disabled={!isActive && !slot.used}
                        onClick={() => openPin(slot.used ? "unpunch" : "punch", "drink", i)}
                        testid={`slot-drink-${i}`}
                        ariaLabel={`Drink ${i + 1}`}
                        index={i + 1}
                      />
                    ))}
                  </div>
                </div>
              )}
              {card.shots.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold mb-2 text-center">
                    Shots
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {card.shots.map((slot, i) => (
                      <PlainSlot
                        key={`shot-${i}`}
                        used={slot.used}
                        disabled={!isActive && !slot.used}
                        onClick={() => openPin(slot.used ? "unpunch" : "punch", "shot", i)}
                        testid={`slot-shot-${i}`}
                        ariaLabel={`Shot ${i + 1}`}
                        index={i + 1}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Live counters — denominators come from the card's own slot arrays
            so each tier shows the correct total (Strip Starter 4/2, Double
            Duo 2/2, VIP Six 6, Classic 6/6). */}
        <div
          className={
            card.shots.length > 0
              ? "mt-5 grid grid-cols-2 gap-3 text-center"
              : "mt-5 grid grid-cols-1 gap-3 text-center"
          }
        >
          <div className="rounded-2xl border border-neon/30 p-3">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/50">
              {t("card.counter.drinks")}
            </div>
            <div className="font-heading text-2xl mt-1 text-neon">{drinksUsed}/{card.drinks.length}</div>
          </div>
          {card.shots.length > 0 && (
            <div className="rounded-2xl border border-neon/30 p-3">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/50">
                {t("card.counter.shots")}
              </div>
              <div className="font-heading text-2xl mt-1 text-neon">{shotsUsed}/{card.shots.length}</div>
            </div>
          )}
        </div>
      </div>

      {/* PIN modal */}
      {pinModal && (
        <div
          data-testid="pin-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 pt-12 bg-black/80 backdrop-blur"
        >
          <form
            onSubmit={submitPin}
            className="surface w-full max-w-sm rounded-4xl p-6 relative"
            style={{
              border: "1.5px solid rgba(255,27,141,0.6)",
              boxShadow: "0 0 32px rgba(255,27,141,0.55)",
            }}
          >
            <button
              type="button"
              onClick={() => setPinModal(null)}
              data-testid="pin-modal-close"
              className="absolute top-3 right-3 text-white/60 hover:text-white"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background:
                    pinModal.mode === "unpunch"
                      ? "linear-gradient(135deg,#FFC857,#F59E0B)"
                      : pinModal.mode === "confirm-final"
                      ? "linear-gradient(135deg,#0aa84a,#22c55e)"
                      : "linear-gradient(135deg,#FF1B8D,#FF6BC6)",
                }}
              >
                <Lock size={18} className="text-black" />
              </div>
              <div>
                <div className="font-heading text-xl uppercase" data-testid="pin-modal-title">
                  {pinModal.mode === "unpunch"
                    ? "Reverse Slot"
                    : pinModal.mode === "confirm-final"
                    ? "Confirm"
                    : t("card.pin.title")}
                </div>
                <div className="text-xs text-white/60 mt-0.5">
                  {pinModal.mode === "confirm-final"
                    ? "Please hand the phone to bar staff."
                    : pinModal.mode === "unpunch"
                    ? `Reverse ${pinModal.slot_type} #${pinModal.slot_index + 1}`
                    : (pinModal.slot_type === "drink"
                        ? t("card.pin.sub.drink")
                        : t("card.pin.sub.shot")) +
                      " #" +
                      (pinModal.slot_index + 1)}
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-white/80">
              {pinModal.mode === "unpunch"
                ? "Please hand the phone to a supervisor to authorise this reversal."
                : pinModal.mode === "confirm-final"
                ? "Please hand the phone to bar staff — this closes the bill and cannot be reversed by front-of-house."
                : t("card.pin.body")}
            </p>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              data-testid="pin-input"
              placeholder="••••"
              className="mt-4 w-full text-center text-2xl tracking-[0.5em] rounded-2xl bg-black/60 border border-white/10 px-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon"
            />
            <button
              type="submit"
              data-testid="pin-submit"
              disabled={submitting || pin.length < 4}
              className="btn-pink mt-5 w-full"
            >
              {submitting ? t("card.pin.submitting") : t("card.pin.submit")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function SlotOverlay({ pos, used, onClick, disabled, testid, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      data-used={used}
      aria-label={ariaLabel}
      className="absolute select-none disabled:cursor-not-allowed transition-transform active:scale-95"
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: `${SLOT_SIZE_PCT}%`,
        aspectRatio: "1 / 1",
        transform: "translate(-50%, -50%)",
        background: "transparent",
        borderRadius: "9999px",
        padding: 0,
        border: 0,
        // Used slots stay tappable so the supervisor can un-punch them.
        boxShadow: disabled ? "none" : "0 0 0 0 rgba(255,27,141,0)",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {used && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <X
            strokeWidth={3}
            style={{
              width: "100%",
              height: "100%",
              color: "#FF1B8D",
              filter:
                "drop-shadow(0 0 10px rgba(255,27,141,0.95)) drop-shadow(0 0 22px rgba(255,27,141,0.7))",
            }}
          />
        </span>
      )}
    </button>
  );
}


function PlainSlot({ used, onClick, disabled, testid, ariaLabel, index }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      data-used={used}
      aria-label={ariaLabel}
      className="relative flex items-center justify-center transition-transform active:scale-95 disabled:cursor-not-allowed"
      style={{
        width: 56,
        height: 56,
        borderRadius: "9999px",
        background: used ? "rgba(255,27,141,0.15)" : "rgba(0,0,0,0.55)",
        border: `2px solid ${used ? "#FF1B8D" : "rgba(255,27,141,0.55)"}`,
        boxShadow: used
          ? "0 0 14px rgba(255,27,141,0.7), inset 0 0 8px rgba(255,27,141,0.35)"
          : "0 0 8px rgba(255,27,141,0.2)",
        color: "#FF1B8D",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !used ? 0.55 : 1,
      }}
    >
      {used ? (
        <X strokeWidth={3} size={30} style={{ filter: "drop-shadow(0 0 6px rgba(255,27,141,0.9))" }} />
      ) : (
        <span className="font-heading text-lg" style={{ color: "rgba(255,255,255,0.75)" }}>{index}</span>
      )}
    </button>
  );
}
